/**
 * The plot-type glyphs, and the order the types are offered in.
 *
 * Drawn rather than lettered: the labels are uppercase mono at 0.64rem and read
 * as five near-identical words in a narrow sidebar, where the shapes are
 * recognisable before the text is. `currentColor` throughout, so whatever
 * colours the surrounding control carries the icon with it.
 *
 * They live here rather than in `plot-controls.ts` because the catalog draws the
 * same shapes on its cards, and a chart that is a scatter in the sidebar
 * but something else in the catalog is two apps. One definition, two callers -
 * `plotTypeIcon()` takes the class so each caller sizes it from its own CSS.
 *
 * The shapes use Lit's `svg` tag, not `html`. A standalone `html` fragment is
 * parsed as HTML, where `<path>` and `<circle>` are unknown elements rather
 * than SVG ones - they mount inside the `<svg>` and draw nothing at all.
 */

import { html, svg, type SVGTemplateResult, type TemplateResult } from 'lit';
import type { PlotType } from '../../api/types.ts';

/** Whether a shape is drawn as solid fill (bars, cells, dots) or as a stroke. */
const FILLED: ReadonlySet<PlotType> = new Set<PlotType>([
  'scatter',
  'heatmap',
  'correlation',
  'histogram',
  'pie',
  'sankey',
]);

const SHAPES: Record<PlotType, SVGTemplateResult> = {
  // A header rule over three rows, split into a key column and values.
  table: svg`<rect x="1.5" y="2" width="12" height="11" rx="0.6" />
    <path d="M1.5 5.5h12M1.5 8.5h12M1.5 11h12M5.5 2v11" opacity="0.8" />`,
  number: svg`<path d="M5.5 1.5 3.5 13.5M11.5 1.5 9.5 13.5M1.5 5.5h12M1 9.5h12" />`,
  // A 270° dial with its needle.
  gauge: svg`<path d="M3.2 12.3A6 6 0 1 1 11.8 12.3" />
    <path d="M7.5 8.5 10.5 5" /><circle cx="7.5" cy="8.5" r="0.9" />`,
  // Two sources flowing into one: bars at the ends, filled ribbons between,
  // each as wide as its source, so it reads as a flow and not as a bracket.
  sankey: svg`<rect x="1" y="1.5" width="2" height="5" rx="0.4" />
    <rect x="1" y="9.5" width="2" height="4" rx="0.4" />
    <rect x="12" y="2.5" width="2" height="10" rx="0.4" />
    <path d="M3 1.5C7.5 1.5 7.5 2.5 12 2.5V7C7.5 7 7.5 6.5 3 6.5Z" opacity="0.55" />
    <path d="M3 9.5C7.5 9.5 7.5 8 12 8V12.5C7.5 12.5 7.5 13.5 3 13.5Z" opacity="0.3" />`,
  line: svg`<path d="M1.5 11.5 5 7l2.5 2.5L13 2.5" />`,
  scatter: svg`<circle cx="3" cy="11" r="1.4" /><circle cx="6.5" cy="7.5" r="1.4" />
    <circle cx="9" cy="9.5" r="1.4" /><circle cx="12" cy="4" r="1.4" />`,
  heatmap: svg`<rect x="1.4" y="8.6" width="3.7" height="3.7" rx="0.4" opacity="0.3" />
    <rect x="5.7" y="8.6" width="3.7" height="3.7" rx="0.4" opacity="0.75" />
    <rect x="10" y="8.6" width="3.7" height="3.7" rx="0.4" opacity="0.18" />
    <rect x="1.4" y="4.3" width="3.7" height="3.7" rx="0.4" opacity="0.5" />
    <rect x="5.7" y="4.3" width="3.7" height="3.7" rx="0.4" />
    <rect x="10" y="4.3" width="3.7" height="3.7" rx="0.4" opacity="0.4" />`,
  // A symmetric matrix: a solid diagonal, with the two halves mirroring.
  correlation: svg`<rect x="1.6" y="1.6" width="3.4" height="3.4" rx="0.4" />
    <rect x="6" y="6" width="3.4" height="3.4" rx="0.4" />
    <rect x="10.4" y="10.4" width="3.4" height="3.4" rx="0.4" />
    <rect x="6" y="1.6" width="3.4" height="3.4" rx="0.4" opacity="0.65" />
    <rect x="1.6" y="6" width="3.4" height="3.4" rx="0.4" opacity="0.65" />
    <rect x="10.4" y="1.6" width="3.4" height="3.4" rx="0.4" opacity="0.25" />
    <rect x="1.6" y="10.4" width="3.4" height="3.4" rx="0.4" opacity="0.25" />
    <rect x="10.4" y="6" width="3.4" height="3.4" rx="0.4" opacity="0.45" />
    <rect x="6" y="10.4" width="3.4" height="3.4" rx="0.4" opacity="0.45" />`,
  histogram: svg`<rect x="1.6" y="7.5" width="3.3" height="5" rx="0.4" />
    <rect x="5.9" y="3.2" width="3.3" height="9.3" rx="0.4" />
    <rect x="10.2" y="5.8" width="3.3" height="6.7" rx="0.4" />`,
  // Three quarters of a disc with the last quarter pulled out a little - the
  // pulled slice is what makes it read as "share" rather than as a dot.
  pie: svg`<path d="M6.9 2.1v6h6A6 6 0 1 1 6.9 2.1Z" />
    <path d="M8.3 0.9a6 6 0 0 1 6 6h-6Z" opacity="0.55" />`,
  // Two boxes with their whiskers and medians - one tall, one short, so it
  // reads as "compare spreads" rather than as two bars.
  box: svg`<path d="M4 1.5v2.5M4 11v2.5M2.5 1.5h3M2.5 13.5h3" />
    <rect x="1.8" y="4" width="4.4" height="7" rx="0.4" />
    <path d="M1.8 7.5h4.4" />
    <path d="M11 3.5v3M11 11v2M9.5 3.5h3M9.5 13h3" />
    <rect x="8.8" y="6.5" width="4.4" height="4.5" rx="0.4" />
    <path d="M8.8 8.5h4.4" />`,
  map: svg`<path d="M7.5 13.5S12 9.4 12 6.2A4.5 4.5 0 0 0 3 6.2c0 3.2 4.5 7.3 4.5 7.3Z" />
    <circle cx="7.5" cy="6" r="1.6" />`,
};

/** One plot-type glyph, sized by whatever class the caller passes. */
export function plotTypeIcon(type: PlotType, className = 'plot-type-icon'): TemplateResult {
  const filled = FILLED.has(type);
  return html`<svg
    class=${className}
    viewBox="0 0 15 15"
    aria-hidden="true"
    fill=${filled ? 'currentColor' : 'none'}
    stroke=${filled ? 'none' : 'currentColor'}
    stroke-width="1.6"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    ${SHAPES[type] ?? SHAPES.line}
  </svg>`;
}

/**
 * The types, in the order the sidebar offers them: four to a row, so the
 * relationship charts sit on top and the distribution and composition ones
 * below.
 */
export const PLOT_TYPES: Array<{ value: PlotType; label: string }> = [
  { value: 'line', label: 'Line' },
  { value: 'scatter', label: 'Scatter' },
  { value: 'heatmap', label: 'Heatmap' },
  { value: 'correlation', label: 'Correlation' },
  { value: 'histogram', label: 'Histogram' },
  { value: 'box', label: 'Box' },
  { value: 'pie', label: 'Pie' },
  { value: 'map', label: 'Map' },
  { value: 'number', label: 'Number' },
  { value: 'gauge', label: 'Gauge' },
  { value: 'sankey', label: 'Sankey' },
  { value: 'table', label: 'Table' },
];
